import React, { useEffect, useState } from 'react';
import {
    Box,
    Paper,
    Typography,
    Button,
    TextField,
    MenuItem,
    Grid,
    Divider,
    IconButton,
    InputAdornment,
} from '@mui/material';
import { useDispatch } from 'react-redux';
import { useNavigate, useParams } from 'react-router-dom';
import { showLoader } from '../../Redux/Slices/loaderSlice';
import {
    createExpense,
    updateExpense,
    getExpenseById,
    getExpenseCategories,
    uploadImage,
} from '../../Redux/Actions/action';
import ErrorHandler from '../../Utils/handler';
import { toast } from 'react-toastify';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import SaveIcon from '@mui/icons-material/Save';
import SendIcon from '@mui/icons-material/Send';
import AttachFileIcon from '@mui/icons-material/AttachFile';
import DeleteIcon from '@mui/icons-material/Delete';
import dayjs from 'dayjs';

const paymentModes = ['Cash', 'Cheque', 'UPI', 'NEFT', 'RTGS', 'IMPS', 'Card', 'Other'];

const ExpenseForm = () => {
    const dispatch = useDispatch();
    const navigate = useNavigate();
    const { id } = useParams();
    const isEdit = Boolean(id);

    const [categories, setCategories] = useState([]);
    const [formData, setFormData] = useState({
        title: '',
        description: '',
        amount: '',
        category: '',
        subCategory: '',
        expenseDate: dayjs().format('YYYY-MM-DD'),
        paymentMode: 'Cash',
        paymentReference: '',
        paidTo: '',
        paidToContact: '',
        invoiceNumber: '',
        invoiceDate: '',
        hasReceipt: false,
        receiptUrl: '',
        attachments: [],
        tags: '',
        department: '',
        project: '',
        budgetHead: '',
    });
    const [errors, setErrors] = useState({});

    // Fetch categories
    useEffect(() => {
        const fetchCategories = async () => {
            try {
                const result = await dispatch(getExpenseCategories({}));
                if (result?.payload?.success) {
                    setCategories(result?.payload?.data || []);
                }
            } catch (error) {
                console.error('Fetch categories error:', error);
            }
        };
        fetchCategories();
    }, [dispatch]);

    // Fetch expense if editing
    useEffect(() => {
        if (isEdit) {
            const fetchExpense = async () => {
                dispatch(showLoader(true));
                try {
                    const result = await dispatch(getExpenseById(id));
                    if (ErrorHandler(result)) {
                        const expense = result?.payload?.data;
                        setFormData({
                            title: expense.title || '',
                            description: expense.description || '',
                            amount: expense.amount || '',
                            category: expense.category?._id || expense.category || '',
                            subCategory: expense.subCategory || '',
                            expenseDate: expense.expenseDate ? dayjs(expense.expenseDate).format('YYYY-MM-DD') : '',
                            paymentMode: expense.paymentMode || 'Cash',
                            paymentReference: expense.paymentReference || '',
                            paidTo: expense.paidTo || '',
                            paidToContact: expense.paidToContact || '',
                            invoiceNumber: expense.invoiceNumber || '',
                            invoiceDate: expense.invoiceDate ? dayjs(expense.invoiceDate).format('YYYY-MM-DD') : '',
                            hasReceipt: expense.hasReceipt || false,
                            receiptUrl: expense.receiptUrl || '',
                            attachments: expense.attachments || [],
                            tags: expense.tags?.join(', ') || '',
                            department: expense.department || '',
                            project: expense.project || '',
                            budgetHead: expense.budgetHead || '',
                        });
                    }
                } catch (error) {
                    toast.error('Failed to fetch expense');
                    navigate('/expenses');
                } finally {
                    dispatch(showLoader(false));
                }
            };
            fetchExpense();
        }
    }, [id, isEdit, dispatch, navigate]);

    const handleChange = (field, value) => {
        setFormData((prev) => ({ ...prev, [field]: value }));
        if (errors[field]) {
            setErrors((prev) => ({ ...prev, [field]: '' }));
        }
    };

    const validate = () => {
        const newErrors = {};
        if (!formData.title.trim()) newErrors.title = 'Title is required';
        if (!formData.amount || formData.amount <= 0) newErrors.amount = 'Valid amount is required';
        if (!formData.category) newErrors.category = 'Category is required';
        if (!formData.expenseDate) newErrors.expenseDate = 'Date is required';
        if (!formData.paidTo.trim()) newErrors.paidTo = 'Paid To is required';
        if (!formData.paymentMode) newErrors.paymentMode = 'Payment mode is required';
        setErrors(newErrors);
        return Object.keys(newErrors).length === 0;
    };

    const handleSubmit = async (status = 'Pending') => {
        if (!validate()) {
            toast.error('Please fill all required fields');
            return;
        }

        dispatch(showLoader(true));
        try {
            const payload = {
                ...formData,
                amount: parseFloat(formData.amount),
                tags: formData.tags ? formData.tags.split(',').map((t) => t.trim()).filter(Boolean) : [],
                status,
            };

            if (isEdit) {
                payload.id = id;
            }

            const action = isEdit ? updateExpense : createExpense;
            const result = await dispatch(action(payload));

            if (ErrorHandler(result)) {
                toast.success(isEdit ? 'Expense updated successfully' : 'Expense created successfully');
                navigate('/expenses');
            }
        } catch (error) {
            toast.error('Failed to save expense');
        } finally {
            dispatch(showLoader(false));
        }
    };

    const handleFileUpload = async (event) => {
        const file = event.target.files[0];
        if (!file) return;

        const formDataUpload = new FormData();
        formDataUpload.append('image', file);

        dispatch(showLoader(true));
        try {
            const result = await dispatch(uploadImage(formDataUpload));
            if (result?.payload?.success) {
                const newAttachment = {
                    fileName: file.name,
                    fileUrl: result?.payload?.data?.url,
                    fileType: file.type,
                    uploadedAt: new Date().toISOString(),
                };
                setFormData((prev) => ({
                    ...prev,
                    attachments: [...prev.attachments, newAttachment],
                    hasReceipt: true,
                }));
                toast.success('File uploaded');
            }
        } catch (error) {
            toast.error('Failed to upload file');
        } finally {
            dispatch(showLoader(false));
        }
    };

    const handleRemoveAttachment = (index) => {
        setFormData((prev) => ({
            ...prev,
            attachments: prev.attachments.filter((_, i) => i !== index),
        }));
    };

    return (
        <Box>
            <Paper elevation={0} sx={{ borderRadius: '24px', padding: '24px' }}>
                {/* Header */}
                <Box display="flex" alignItems="center" gap={2} mb={3}>
                    <IconButton onClick={() => navigate('/expenses')}>
                        <ArrowBackIcon />
                    </IconButton>
                    <Typography variant="h5" fontWeight={600}>
                        {isEdit ? 'Edit Expense' : 'Add New Expense'}
                    </Typography>
                </Box>

                <Divider sx={{ mb: 3 }} />

                {/* Form */}
                <Grid container spacing={3}>
                    {/* Basic Info */}
                    <Grid item xs={12}>
                        <Typography variant="subtitle1" fontWeight={600} color="primary" mb={2}>
                            Basic Information
                        </Typography>
                    </Grid>

                    <Grid item xs={12} md={6}>
                        <TextField
                            fullWidth
                            label="Title *"
                            value={formData.title}
                            onChange={(e) => handleChange('title', e.target.value)}
                            error={Boolean(errors.title)}
                            helperText={errors.title}
                            placeholder="e.g., Office Stationery Purchase"
                        />
                    </Grid>

                    <Grid item xs={12} md={6}>
                        <TextField
                            fullWidth
                            label="Amount *"
                            type="number"
                            value={formData.amount}
                            onChange={(e) => handleChange('amount', e.target.value)}
                            error={Boolean(errors.amount)}
                            helperText={errors.amount}
                            InputProps={{
                                startAdornment: <InputAdornment position="start">₹</InputAdornment>,
                            }}
                        />
                    </Grid>

                    <Grid item xs={12} md={6}>
                        <TextField
                            fullWidth
                            select
                            label="Category *"
                            value={formData.category}
                            onChange={(e) => handleChange('category', e.target.value)}
                            error={Boolean(errors.category)}
                            helperText={errors.category}
                        >
                            {categories.map((cat) => (
                                <MenuItem key={cat._id} value={cat._id}>
                                    {cat.icon} {cat.name}
                                </MenuItem>
                            ))}
                        </TextField>
                    </Grid>

                    <Grid item xs={12} md={6}>
                        <TextField
                            fullWidth
                            type="date"
                            label="Expense Date *"
                            value={formData.expenseDate}
                            onChange={(e) => handleChange('expenseDate', e.target.value)}
                            error={Boolean(errors.expenseDate)}
                            helperText={errors.expenseDate}
                            InputLabelProps={{ shrink: true }}
                        />
                    </Grid>

                    <Grid item xs={12}>
                        <TextField
                            fullWidth
                            label="Description"
                            value={formData.description}
                            onChange={(e) => handleChange('description', e.target.value)}
                            multiline
                            rows={3}
                            placeholder="Detailed description of the expense..."
                        />
                    </Grid>

                    {/* Payment Details */}
                    <Grid item xs={12} mt={2}>
                        <Typography variant="subtitle1" fontWeight={600} color="primary" mb={2}>
                            Payment Details
                        </Typography>
                    </Grid>

                    <Grid item xs={12} md={6}>
                        <TextField
                            fullWidth
                            label="Paid To *"
                            value={formData.paidTo}
                            onChange={(e) => handleChange('paidTo', e.target.value)}
                            error={Boolean(errors.paidTo)}
                            helperText={errors.paidTo}
                            placeholder="Vendor/Person name"
                        />
                    </Grid>

                    <Grid item xs={12} md={6}>
                        <TextField
                            fullWidth
                            label="Paid To Contact"
                            value={formData.paidToContact}
                            onChange={(e) => handleChange('paidToContact', e.target.value)}
                            placeholder="Phone/Email"
                        />
                    </Grid>

                    <Grid item xs={12} md={6}>
                        <TextField
                            fullWidth
                            select
                            label="Payment Mode *"
                            value={formData.paymentMode}
                            onChange={(e) => handleChange('paymentMode', e.target.value)}
                            error={Boolean(errors.paymentMode)}
                            helperText={errors.paymentMode}
                        >
                            {paymentModes.map((mode) => (
                                <MenuItem key={mode} value={mode}>{mode}</MenuItem>
                            ))}
                        </TextField>
                    </Grid>

                    <Grid item xs={12} md={6}>
                        <TextField
                            fullWidth
                            label="Payment Reference"
                            value={formData.paymentReference}
                            onChange={(e) => handleChange('paymentReference', e.target.value)}
                            placeholder="Transaction ID, Cheque No, etc."
                        />
                    </Grid>

                    {/* Invoice Details */}
                    <Grid item xs={12} mt={2}>
                        <Typography variant="subtitle1" fontWeight={600} color="primary" mb={2}>
                            Invoice / Receipt Details
                        </Typography>
                    </Grid>

                    <Grid item xs={12} md={6}>
                        <TextField
                            fullWidth
                            label="Invoice Number"
                            value={formData.invoiceNumber}
                            onChange={(e) => handleChange('invoiceNumber', e.target.value)}
                        />
                    </Grid>

                    <Grid item xs={12} md={6}>
                        <TextField
                            fullWidth
                            type="date"
                            label="Invoice Date"
                            value={formData.invoiceDate}
                            onChange={(e) => handleChange('invoiceDate', e.target.value)}
                            InputLabelProps={{ shrink: true }}
                        />
                    </Grid>

                    <Grid item xs={12}>
                        <Box>
                            <Button
                                variant="outlined"
                                component="label"
                                startIcon={<AttachFileIcon />}
                            >
                                Upload Receipt/Invoice
                                <input type="file" hidden onChange={handleFileUpload} accept="image/*,.pdf" />
                            </Button>
                            {formData.attachments.length > 0 && (
                                <Box mt={2}>
                                    {formData.attachments.map((att, index) => (
                                        <Box
                                            key={index}
                                            display="flex"
                                            alignItems="center"
                                            gap={1}
                                            p={1}
                                            bgcolor="#f5f5f5"
                                            borderRadius={1}
                                            mb={1}
                                        >
                                            <AttachFileIcon fontSize="small" />
                                            <Typography variant="body2" flex={1}>{att.fileName}</Typography>
                                            <IconButton size="small" onClick={() => handleRemoveAttachment(index)}>
                                                <DeleteIcon fontSize="small" color="error" />
                                            </IconButton>
                                        </Box>
                                    ))}
                                </Box>
                            )}
                        </Box>
                    </Grid>

                    {/* Additional Info */}
                    <Grid item xs={12} mt={2}>
                        <Typography variant="subtitle1" fontWeight={600} color="primary" mb={2}>
                            Additional Information (Optional)
                        </Typography>
                    </Grid>

                    <Grid item xs={12} md={4}>
                        <TextField
                            fullWidth
                            label="Department"
                            value={formData.department}
                            onChange={(e) => handleChange('department', e.target.value)}
                        />
                    </Grid>

                    <Grid item xs={12} md={4}>
                        <TextField
                            fullWidth
                            label="Project"
                            value={formData.project}
                            onChange={(e) => handleChange('project', e.target.value)}
                        />
                    </Grid>

                    <Grid item xs={12} md={4}>
                        <TextField
                            fullWidth
                            label="Budget Head"
                            value={formData.budgetHead}
                            onChange={(e) => handleChange('budgetHead', e.target.value)}
                        />
                    </Grid>

                    <Grid item xs={12}>
                        <TextField
                            fullWidth
                            label="Tags"
                            value={formData.tags}
                            onChange={(e) => handleChange('tags', e.target.value)}
                            placeholder="Comma separated tags (e.g., urgent, quarterly)"
                        />
                    </Grid>
                </Grid>

                {/* Actions */}
                <Box display="flex" justifyContent="flex-end" gap={2} mt={4}>
                    <Button variant="outlined" onClick={() => navigate('/expenses')}>
                        Cancel
                    </Button>
                    <Button
                        variant="outlined"
                        startIcon={<SaveIcon />}
                        onClick={() => handleSubmit('Draft')}
                    >
                        Save as Draft
                    </Button>
                    <Button
                        variant="contained"
                        startIcon={<SendIcon />}
                        onClick={() => handleSubmit('Pending')}
                    >
                        {isEdit ? 'Update & Submit' : 'Submit for Approval'}
                    </Button>
                </Box>
            </Paper>
        </Box>
    );
};

export default ExpenseForm;
